import React from 'react';
import { Sparkles } from 'lucide-react';
import './ChatMessage.css';

export interface ChatMessageProps {
  type: 'assistant' | 'user-response' | 'question' | 'loading';
  content: string;
  questionNumber?: number;
  totalQuestions?: number;
  avatar?: React.ReactNode;
  author?: string;
  timestamp?: string;
}

const ChatMessageAvatar: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="chat-message-avatar">
    <div className="chat-message-avatar-circle">
      {children || (
        <Sparkles size={16} className="chat-message-avatar-icon" />
      )}
    </div>
  </div>
);

const ChatMessageHeader: React.FC<{ avatar?: React.ReactNode; author?: string }> = ({ avatar, author }) => (
  <div className="chat-message-header">
    <ChatMessageAvatar>{avatar}</ChatMessageAvatar>
    {author && <div className="chat-message-author">{author}</div>}
  </div>
);

export const ChatMessage: React.FC<ChatMessageProps> = ({
  type,
  content,
  questionNumber,
  totalQuestions,
  avatar,
  author,
  timestamp,
}) => {
  const renderAssistantMessage = () => (
    <div className="chat-message chat-message--assistant">
      <div className="chat-message-container">
        <ChatMessageHeader avatar={avatar} author={author} />
        <div className="chat-message-content">
          <div className="chat-message-bubble">
            {content}
          </div>
          {timestamp && <div className="chat-message-timestamp">{timestamp}</div>}
        </div>
      </div>
    </div>
  );

  const renderQuestionMessage = () => (
    <div className="chat-message chat-message--question">
      <div className="chat-message-content">
        <div className="chat-message-question-header">
          QUESTION {questionNumber}/{totalQuestions}:
        </div>
        <div className="chat-message-question-bubble">
          {content}
        </div>
        {timestamp && <div className="chat-message-timestamp">{timestamp}</div>}
      </div>
    </div>
  );

  const renderUserResponse = () => (
    <div className="chat-message chat-message--user-response">
      <div className="chat-message-content">
        <div className="chat-message-user-response-bubble">
          {content}
        </div>
        {timestamp && <div className="chat-message-timestamp">{timestamp}</div>}
      </div>
    </div>
  );

  const renderLoadingMessage = () => (
    <div className="chat-message chat-message--loading">
      <div className="chat-message-container">
        <ChatMessageHeader avatar={avatar} author={author} />
        <div className="chat-message-content">
          <div className="chat-message-bubble">
            <div className="chat-message-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  switch (type) {
    case 'assistant':
      return renderAssistantMessage();
    case 'question':
      return renderQuestionMessage();
    case 'user-response':
      return renderUserResponse();
    case 'loading':
      return renderLoadingMessage();
    default:
      return renderAssistantMessage();
  }
};

export default ChatMessage;
